import { useState } from "react";
import { upsertCar, deleteCar, newId, type CanonicalStore, type Car } from "../../core/index.ts";
import { Modal, TextField, NumberField, SelectField, FormActions, parseNum, numToStr } from "./Modal.tsx";
import { PhotoField, SingleDocField } from "./Attachments.tsx";

const FUEL_TYPES = ["Petrol", "Diesel", "Hybrid (eHEV)", "Plug-in Hybrid", "Electric", "LPG"];

export function CarForm({ store, editing, onSave, onClose }: {
  store: CanonicalStore; editing?: Car; onSave: (s: CanonicalStore) => void; onClose: () => void;
}) {
  const currency = store.profile.displayCurrency;
  const [make, setMake] = useState(editing?.make ?? "");
  const [model, setModel] = useState(editing?.model ?? "");
  const [year, setYear] = useState(editing?.year ? String(editing.year) : "");
  const [plate, setPlate] = useState(editing?.licensePlate ?? "");
  const [color, setColor] = useState(editing?.color ?? "");
  const [fuelType, setFuelType] = useState(editing?.fuelType ?? "Petrol");
  const [tankVolume, setTank] = useState(numToStr(editing?.tankVolume));
  const [batteryCapacity, setBatt] = useState(numToStr(editing?.batteryCapacity));
  const [mfgFuel, setMfgFuel] = useState(numToStr(editing?.manufacturerAvgConsumption));
  const [mfgEnergy, setMfgEnergy] = useState(numToStr(editing?.manufacturerAvgEnergy));
  const [insurance, setInsurance] = useState(numToStr(editing?.insuranceAmount));
  const [roadTax, setRoadTax] = useState(numToStr(editing?.roadTaxAmount));
  const [purchase, setPurchase] = useState(numToStr(editing?.purchasePrice));
  const [photos, setPhotos] = useState<string[]>(editing?.photoFilenames ?? []);
  const [insuranceDoc, setInsuranceDoc] = useState<string | null>(editing?.insuranceDocumentFilename ?? null);
  const [roadTaxDoc, setRoadTaxDoc] = useState<string | null>(editing?.roadTaxDocumentFilename ?? null);

  const optNum = (s: string): number | null => (s.trim() === "" ? null : parseNum(s));

  return (
    <Modal title={editing ? "Edit car" : "Add car"} onClose={onClose}>
      <TextField label="Make" value={make} onChange={setMake} />
      <TextField label="Model" value={model} onChange={setModel} />
      <NumberField label="Year" value={year} onChange={setYear} />
      <TextField label="Plate" value={plate} onChange={setPlate} />
      <TextField label="Color" value={color} onChange={setColor} />
      <SelectField label="Fuel type" value={fuelType} onChange={setFuelType} options={FUEL_TYPES.includes(fuelType) ? FUEL_TYPES : [fuelType, ...FUEL_TYPES]} />
      <NumberField label="Tank volume (L)" value={tankVolume} onChange={setTank} />
      <NumberField label="Battery capacity (kWh)" value={batteryCapacity} onChange={setBatt} />
      <NumberField label="Mfg avg fuel (L/100km)" value={mfgFuel} onChange={setMfgFuel} />
      <NumberField label="Mfg avg energy (kWh/100km)" value={mfgEnergy} onChange={setMfgEnergy} />
      <NumberField label={`Insurance (${currency}/yr)`} value={insurance} onChange={setInsurance} />
      <NumberField label={`Road tax (${currency}/yr)`} value={roadTax} onChange={setRoadTax} />
      <NumberField label={`Purchase price (${currency})`} value={purchase} onChange={setPurchase} />
      <PhotoField label="Vehicle photos" filenames={photos} onChange={setPhotos} />
      <SingleDocField label="Insurance document" filename={insuranceDoc} onChange={setInsuranceDoc} />
      <SingleDocField label="Road tax document" filename={roadTaxDoc} onChange={setRoadTaxDoc} />
      <FormActions
        canSave={make.trim() !== "" || model.trim() !== ""}
        onSave={() => {
          const base = editing ?? ({ id: newId(), vehicleType: "car", autoTripRecording: false, photoFilenames: [] } as Car);
          const car: Car = {
            ...base,
            make: make.trim(), model: model.trim(), year: optNum(year) ?? undefined,
            licensePlate: plate.trim() || null, color: color.trim() || null, fuelType,
            tankVolume: optNum(tankVolume), batteryCapacity: optNum(batteryCapacity),
            manufacturerAvgConsumption: optNum(mfgFuel), manufacturerAvgEnergy: optNum(mfgEnergy),
            insuranceAmount: optNum(insurance), roadTaxAmount: optNum(roadTax), purchasePrice: optNum(purchase),
            purchaseCurrency: currency,
            photoFilenames: photos,
            insuranceDocumentFilename: insuranceDoc,
            roadTaxDocumentFilename: roadTaxDoc,
          };
          onSave(upsertCar(store, car)); onClose();
        }}
        onDelete={editing && store.cars.length > 1 ? () => {
          if (confirm("Delete this car and all its data?")) { onSave(deleteCar(store, editing.id)); onClose(); }
        } : undefined}
      />
    </Modal>
  );
}
